import { useEffect, useState } from "react";

import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import axios from "axios";
import { usePostHog } from "posthog-js/react";
import { useLocation } from "react-router-dom";

import { CompanyLogoTile } from "components/CompanyLogoTile";
import { ErrorTypography } from "components/ErrorTypography";
import { LoadingWrappedNode } from "components/LoadingWrappedNode";
import { ClientAppEvent, LOGO_ALLOWED_INPUT_TYPES } from "constants/index";
import { useAPI, useAuthContext, useWriterComponentState } from "hooks";
import { extractAPIRequestErrorMessage } from "utils";
import { normalizeLogoToPng, uploadFileToS3 } from "utils/fileUpload";

import type { CompanyProfile } from "types/company";

const LOGO_PREVIEW_SIZE_PX = 120;
const SAVE_FAILED_MESSAGE = "The logo could not be saved. Please try again.";
const REMOVE_FAILED_MESSAGE =
  "The logo could not be removed. Please try again.";

export type LogoActionKind = "updated" | "removed";

/**
 * `normalizeLogoToPng` and the S3 helper raise messages written for the user
 * ("Choose a PNG, JPEG or WEBP image."), so those surface as-is. An axios
 * rejection is also an Error, but its `.message` is the useless "Request failed
 * with status code 400" — the backend's message lives under `response.data`,
 * which `extractAPIRequestErrorMessage` knows how to read.
 */
function resolveSaveErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    return extractAPIRequestErrorMessage(error, SAVE_FAILED_MESSAGE);
  }
  return error instanceof Error ? error.message : SAVE_FAILED_MESSAGE;
}

interface Props {
  companyProfile: CompanyProfile;
  modalOpen: boolean;
  onModalClose: VoidFunction;
  onLogoActionSuccess: (kind: LogoActionKind) => void;
}

interface CreateLogoUploadUrlResponse {
  file_pk: number;
  upload_url: string;
}

export function EditCompanyLogoModal(props: Props) {
  const { companyProfile, modalOpen, onModalClose, onLogoActionSuccess } =
    props;
  const { api } = useAPI();
  const { user } = useAuthContext();
  const posthog = usePostHog();
  const location = useLocation();

  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [pendingPreviewUrl, setPendingPreviewUrl] = useState<string | null>(
    null,
  );
  const [willRemove, setWillRemove] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const companyId = companyProfile.company.id;
  const currentLogoUrl = companyProfile.logo?.download_url ?? null;

  // Removal is a single request, so it goes through the shared writer hook per
  // docs/engineering/design/carrier_company_logo_phase_2_design.md §5.4. The upload chain
  // cannot: the hook binds one url/method per instance and clears `loading` in
  // each call's `finally`, so chaining presign -> S3 PUT -> commit through three
  // instances would re-enable Save mid-flight and admit a duplicate submit.
  const removeLogoWriter = useWriterComponentState({
    errorMessage: REMOVE_FAILED_MESSAGE,
    writeConfig: {
      method: "delete",
      url: `/companies/${companyId}/company-logo`,
    },
  });

  // Blob URLs are revoked whenever the pick is replaced and on unmount, so
  // repeated edits in one session don't leak them.
  useEffect(
    () => () => {
      if (pendingPreviewUrl !== null) URL.revokeObjectURL(pendingPreviewUrl);
    },
    [pendingPreviewUrl],
  );

  const resetPendingState = () => {
    setPendingFile(null);
    setPendingPreviewUrl(null);
    setWillRemove(false);
    setError(null);
    removeLogoWriter.resetState();
  };

  const handleClose = () => {
    resetPendingState();
    onModalClose();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // Clear the input so picking the same file again still fires change —
    // browsers suppress it when the value is unchanged, which would otherwise
    // strand a user who re-picks the same file after a validation error.
    // eslint-disable-next-line no-param-reassign
    event.target.value = "";
    if (file === undefined) return;

    setError(null);
    setWillRemove(false);
    setPendingFile(file);
    setPendingPreviewUrl(URL.createObjectURL(file));
  };

  const handleRemoveClick = () => {
    setError(null);
    setPendingFile(null);
    setPendingPreviewUrl(null);
    setWillRemove(true);
  };

  const uploadAndCommit = async (file: File) => {
    const normalized = await normalizeLogoToPng(file);

    const urlResponse = await api<CreateLogoUploadUrlResponse>(
      "post",
      `/companies/${companyId}/create-company-logo-upload-url`,
      {
        original_filename: normalized.name,
        content_type: normalized.type,
      },
    );
    const { file_pk: filePk, upload_url: uploadUrl } = urlResponse.data;

    const result = await uploadFileToS3(
      normalized,
      uploadUrl,
      user?.id?.toString() ?? "",
    );
    if (!result.success) {
      throw new Error("The logo could not be uploaded. Please try again.");
    }

    await api("post", `/companies/${companyId}/commit-company-logo`, {
      file_pk: filePk,
    });
  };

  const saveNewLogo = async (file: File) => {
    setError(null);
    setLoading(true);

    try {
      await uploadAndCommit(file);
      posthog?.capture(ClientAppEvent.CARRIER_COMPANY_LOGO_UPDATED, {
        path: location.pathname,
      });
      onLogoActionSuccess("updated");
      resetPendingState();
      onModalClose();
    } catch (e) {
      setError(resolveSaveErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (willRemove) {
      removeLogoWriter.write(() => {
        posthog?.capture(ClientAppEvent.CARRIER_COMPANY_LOGO_REMOVED, {
          path: location.pathname,
        });
        onLogoActionSuccess("removed");
        resetPendingState();
        onModalClose();
      });
      return;
    }

    if (pendingFile !== null) {
      await saveNewLogo(pendingFile);
    }
  };

  const previewUrl = willRemove ? null : (pendingPreviewUrl ?? currentLogoUrl);
  const hasPendingChange = willRemove || pendingFile !== null;
  const saving = loading || removeLogoWriter.loading;
  const displayedError = error ?? removeLogoWriter.error;

  return (
    <Dialog open={modalOpen} onClose={handleClose} fullWidth maxWidth="xs">
      <DialogTitle>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          Update Company Logo
          <IconButton onClick={handleClose} aria-label="Close">
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent>
        <LoadingWrappedNode loading={saving}>
          <Stack spacing={2} alignItems="center" sx={{ py: 1 }}>
            <CompanyLogoTile
              companyName={companyProfile.company.name}
              logoDownloadUrl={previewUrl}
              size={LOGO_PREVIEW_SIZE_PX}
            />

            <Button variant="outlined" component="label">
              Choose File
              <input
                type="file"
                hidden
                accept={LOGO_ALLOWED_INPUT_TYPES.join(",")}
                onChange={handleFileChange}
              />
            </Button>

            <Typography variant="caption" color="text.secondary">
              Recommended: 400×400px, transparent background.
            </Typography>

            {currentLogoUrl !== null && !willRemove && (
              <Button
                color="error"
                startIcon={<DeleteOutlineIcon />}
                onClick={handleRemoveClick}
              >
                Remove logo
              </Button>
            )}

            {displayedError !== null && (
              <ErrorTypography error={displayedError} />
            )}
          </Stack>
        </LoadingWrappedNode>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || !hasPendingChange}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
